import { Link } from "react-router-dom";
import { Fragment } from "react";
import {
  ArrowRight, ArrowUpRight, Check, Minus, Star, ShieldCheck,
  Globe, Workflow, Users, TrendingUp, Target, FileSignature, Handshake,
  Smartphone, Database, Mail, BarChart3, Search, Building2, Wallet, Settings,
  Wrench, Zap,
} from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import FAQ from "@/components/FAQ";

// À la carte services — published "from" prices, every service undercuts the
// typical Australian SME-market band (sourced from public agency rate cards).
// Numbers are quoted higher case-by-case after the Strategy Call.
const ALA_CARTE = [
  {
    id: "diagnostic",
    name: "Diagnostic Sprint",
    icon: Search,
    desc: "One-week deep dive across financials, sales, ops and systems. Written 12-page report with the three highest-leverage moves.",
    price: "AU$997",
    market: "Typical: $2,500–$5,000",
    savings: "Save up to AU$4,000",
    timeframe: "1 week",
    tag: "Entry point",
  },
  {
    id: "website",
    name: "Website Build",
    icon: Globe,
    desc: "Modern, conversion-focused site. Custom design, mobile-first, fast hosting, web forms with smart routing, baseline SEO.",
    price: "From AU$2,997",
    market: "Typical agency: $6,000–$15,000",
    savings: "Save AU$3k–$12k",
    timeframe: "3–4 weeks",
  },
  {
    id: "crm",
    name: "CRM Setup & Migration",
    icon: Database,
    desc: "HubSpot, Pipedrive or Salesforce configured to your job lifecycle. Pipeline stages, automation, data migration, team training.",
    price: "From AU$1,997",
    market: "Typical: $4,000–$8,000",
    savings: "Save AU$2k–$6k",
    timeframe: "2 weeks",
  },
  {
    id: "app",
    name: "Mobile / Web App MVP",
    icon: Smartphone,
    desc: "Functional MVP of a custom customer or operations app. Includes design, build, hosting setup and handover documentation.",
    price: "From AU$7,997",
    market: "Typical agency: $20,000–$60,000",
    savings: "Save AU$12k–$52k",
    timeframe: "6–8 weeks",
  },
  {
    id: "automation",
    name: "Marketing Automation",
    icon: Zap,
    desc: "End-to-end automated nurture: quote follow-up, missed-call SMS, review requests, win-back sequences. Wired to your CRM.",
    price: "From AU$1,997",
    market: "Typical: $3,500–$10,000",
    savings: "Save AU$1.5k–$8k",
    timeframe: "2 weeks",
  },
  {
    id: "sales-process",
    name: "Sales Process Buildout",
    icon: TrendingUp,
    desc: "Pricing model, qualification scripts, quote templates, pipeline stages, conversion triggers. Sales playbook your team can run.",
    price: "From AU$2,997",
    market: "Typical: $5,000–$15,000",
    savings: "Save AU$2k–$12k",
    timeframe: "3 weeks",
  },
  {
    id: "seo",
    name: "SEO Foundation",
    icon: Search,
    desc: "Technical audit, on-page fixes, Google Business optimisation, schema markup, local citations, content roadmap.",
    price: "From AU$997",
    market: "Typical: $1,500–$5,000 + monthly",
    savings: "Save up to AU$4,000",
    timeframe: "2 weeks",
  },
  {
    id: "financial",
    name: "Financial Strategy & Cashflow Model",
    icon: Wallet,
    desc: "13-week rolling cashflow, gross-margin model, pricing review, scenario planning. Built in Google Sheets you can run yourself.",
    price: "From AU$1,997",
    market: "Typical: $3,000–$10,000",
    savings: "Save AU$1k–$8k",
    timeframe: "2 weeks",
  },
  {
    id: "operations",
    name: "Operations Playbook",
    icon: Wrench,
    desc: "Standard operating procedures, job-flow documentation, role clarity, handover checklists. The system your team follows when you're not there.",
    price: "From AU$2,997",
    market: "Typical: $5,000–$15,000",
    savings: "Save AU$2k–$12k",
    timeframe: "3 weeks",
  },
  {
    id: "brand",
    name: "Brand Identity & Visual System",
    icon: Star,
    desc: "Logo, type system, colour palette, brand guidelines, business card, email signature, social templates. Production-ready files.",
    price: "From AU$1,497",
    market: "Typical: $2,500–$10,000",
    savings: "Save AU$1k–$8.5k",
    timeframe: "2 weeks",
  },
  {
    id: "fractional",
    name: "Fractional CFO / CMO",
    icon: Building2,
    desc: "Senior advisor on retainer: 2 strategy sessions per month, async access between sessions, quarterly board pack preparation.",
    price: "From AU$2,497/mo",
    market: "Typical: $5,000–$15,000/mo",
    savings: "Save AU$2.5k–$12.5k/mo",
    timeframe: "Monthly rolling",
  },
  {
    id: "reporting",
    name: "KPI Dashboard Build",
    icon: BarChart3,
    desc: "Real-time KPI dashboard wired to your data sources. Revenue, conversion, margin, ops metrics — viewable on web and mobile.",
    price: "From AU$1,497",
    market: "Typical: $3,000–$8,000",
    savings: "Save AU$1.5k–$6.5k",
    timeframe: "2 weeks",
  },
];

const GROW_WITH_YOU = {
  id: "grow-with-you",
  name: "Grow-With-You",
  subtitle: "Performance-aligned engagement",
  pitch:
    "For operators who want maximum alignment. A lower fixed base, plus a performance fee tied to incremental gross profit and a small set of KPI uplifts we agree before we start. We earn more when you do — and we only earn the upside when it's real.",
  pillars: [
    {
      icon: ShieldCheck,
      title: "Lower fixed base",
      body: "Reduced monthly retainer relative to the equivalent Full Execution tier. The cost of access is intentionally lower so the alignment is real.",
    },
    {
      icon: TrendingUp,
      title: "Performance fee on gross profit uplift",
      body: "An agreed percentage of incremental gross profit above a written baseline. Margin uplift, not vanity revenue — the metric that actually compounds.",
    },
    {
      icon: Target,
      title: "Outcome bonuses on KPIs",
      body: "Modest one-time bonuses tied to a short list of measurable KPIs you agree at engagement kickoff (e.g. lead-to-job conversion, quote-to-win rate, EBITDA uplift).",
    },
    {
      icon: FileSignature,
      title: "Written baseline & attribution clause",
      body: "Baseline locked in writing before work begins. Attribution methodology agreed upfront so the share is calculated cleanly, with no end-of-quarter surprises.",
    },
  ],
  fitFor:
    "Established operators with $1.5M+ in revenue, durable margin, clean financials and the willingness to share upside in exchange for stronger alignment.",
  notFitFor:
    "Early-stage businesses still finding product-market fit, operators chasing top-line growth at any cost, or anyone uncomfortable opening their P&L for baseline calculation.",
  cta: { label: "Discuss Grow-With-You", to: "/contact" },
};

const TIERS = [
  {
    id: "foundation",
    name: "Foundation",
    subtitle: "Website + Lead Capture",
    icon: Globe,
    pitch:
      "A modern, conversion-focused presence that turns existing demand into booked work. The right starting point for established operators with momentum but an outdated digital front door.",
    setup: "From AU$7,500",
    monthly: "AU$750",
    monthlyNote: "per month",
    features: [
      "Custom, conversion-focused website",
      "Local SEO foundation (Google Business, schema)",
      "Web forms with smart routing",
      "Missed-call SMS auto-response",
      "Basic lead dashboard",
      "Monthly performance report",
    ],
    outcome: "More of your existing traffic and missed calls turn into booked work.",
    badge: null,
    cta: { label: "Book a Strategy Call", to: "/contact" },
  },
  {
    id: "growth-system",
    name: "Growth System",
    subtitle: "Website + CRM + Automation",
    icon: Workflow,
    pitch:
      "Our flagship. Everything in Foundation, plus a full CRM, automated follow-up workflows, KPI dashboards and an AI chat assistant. The system most clients should start with.",
    setup: "From AU$19,500",
    monthly: "AU$2,950",
    monthlyNote: "per month",
    features: [
      "Everything in Foundation",
      "Full CRM configured to your job lifecycle",
      "Automated quote and review follow-up sequences",
      "Real-time KPI dashboards (web + mobile)",
      "AI chat assistant, trained on your business",
      "Quarterly strategy review with a senior advisor",
    ],
    outcome:
      "Lead-to-job conversion typically lifts 2 to 4 times within 90 days. Owner admin hours fall sharply.",
    badge: "Most popular",
    cta: { label: "Book a Strategy Call", to: "/contact" },
  },
  {
    id: "full-execution",
    name: "Full Execution",
    subtitle: "Growth System + Outsourced Operations",
    icon: Users,
    pitch:
      "The complete operating system, run for you. Everything in Growth System, plus our outsourced team handling daily follow-up, ad management, content, reviews and reporting. You stay on the work that matters.",
    setup: "From AU$32,000",
    monthly: "AU$5,950",
    monthlyNote: "per month",
    features: [
      "Everything in Growth System",
      "Outsourced execution team (15 to 30 hrs / week)",
      "AI voice assistant for after-hours bookings",
      "Daily CRM hygiene & quote follow-up calls",
      "Ad campaign management (Google + Meta)",
      "Monthly strategic review + quarterly roadmap",
    ],
    outcome:
      "60 to 80 percent reduction in owner admin hours. System runs whether you are there or not.",
    badge: null,
    cta: { label: "Book a Strategy Call", to: "/contact" },
  },
];

const COMPARISON_GROUPS = [
  {
    group: "Website & Capture",
    rows: [
      { feature: "Custom, conversion-focused website", tiers: [true, true, true] },
      { feature: "Local SEO foundation", tiers: [true, true, true] },
      { feature: "Web forms + smart routing", tiers: [true, true, true] },
      { feature: "Missed-call SMS auto-response", tiers: [true, true, true] },
    ],
  },
  {
    group: "CRM & Automation",
    rows: [
      { feature: "CRM configured to your business", tiers: [false, true, true] },
      { feature: "Automated follow-up sequences", tiers: [false, true, true] },
      { feature: "Job lifecycle automation", tiers: [false, true, true] },
      { feature: "Review request automation", tiers: [false, true, true] },
    ],
  },
  {
    group: "Reporting & Intelligence",
    rows: [
      { feature: "Basic lead dashboard", tiers: [true, true, true] },
      { feature: "Real-time KPI dashboards (web + mobile)", tiers: [false, true, true] },
      { feature: "Margin & profitability by job type", tiers: [false, true, true] },
      { feature: "Monthly performance report", tiers: [true, true, true] },
    ],
  },
  {
    group: "AI Support",
    rows: [
      { feature: "AI chat assistant (web)", tiers: [false, true, true] },
      { feature: "AI voice assistant (after-hours bookings)", tiers: [false, false, true] },
      { feature: "Custom AI knowledge base", tiers: [false, true, true] },
    ],
  },
  {
    group: "Outsourced Execution",
    rows: [
      { feature: "Daily CRM hygiene", tiers: [false, false, true] },
      { feature: "Quote follow-up calls (human)", tiers: [false, false, true] },
      { feature: "Ad campaign management", tiers: [false, false, true] },
      { feature: "Content posting & review chasing", tiers: [false, false, true] },
    ],
  },
  {
    group: "Strategic Advisory",
    rows: [
      { feature: "Annual strategy review", tiers: [true, false, false] },
      { feature: "Quarterly strategy review (senior advisor)", tiers: [false, true, false] },
      { feature: "Monthly strategy review + quarterly roadmap", tiers: [false, false, true] },
    ],
  },
];

const OUTCOMES = [
  { k: "2 to 4 times", v: "Typical lift in quote-to-job conversion within 90 days." },
  { k: "60 to 80%", v: "Reduction in owner and partner admin hours each week." },
  { k: "Same day", v: "Quote follow-up cadence, every lead, every time." },
  { k: "1 view", v: "Single dashboard for the whole business." },
];

const FAQS = [
  {
    q: "How does Grow-With-You actually work?",
    a: "You pay a reduced fixed monthly base, plus an agreed percentage of incremental gross profit above a written baseline, plus modest one-time bonuses on a short list of KPI uplifts. The baseline, percentage and attribution methodology are all locked in writing before any work begins, so the calculation is unambiguous every quarter.",
  },
  {
    q: "Is Grow-With-You equity, profit-share or an investment?",
    a: "No. It is a consulting fee structure where part of the fee is tied to measurable performance outcomes. Toulios Advisory Group does not take equity, does not invest capital, does not act as a securities issuer, and does not hold an Australian Financial Services Licence. The engagement is general management advisory only.",
  },
  {
    q: "Why gross profit instead of revenue?",
    a: "Revenue can be inflated through unprofitable jobs, deep discounts or volume-at-any-cost behaviour. Gross profit is the real signal of business health. Tying the performance share to gross profit uplift keeps both sides focused on the right number.",
  },
  {
    q: "Who is Grow-With-You for?",
    a: "Established operators (typically $1.5M+ in revenue) with durable margin, clean financial reporting, and a willingness to share upside in exchange for stronger alignment. It is not for early-stage businesses still finding product-market fit, or for operators uncomfortable opening their P&L for baseline calculation.",
  },
  {
    q: "Are the prices on this page final?",
    a: "These are indicative starting prices for the productised tiers. Every engagement, productised or performance-aligned, is scoped tightly during the Strategy Call and quoted in writing as a fixed fee (or fixed base + performance terms) before any work begins. There are no hidden costs and no scope creep.",
  },
  {
    q: "What payment terms apply?",
    a: "Setup fees: 50 percent on signing, 50 percent on go-live (Net 14). Monthly fees: invoiced in advance, Net 14, monthly rolling after the first 90 days. Performance share on Grow-With-You is invoiced in arrears at the end of each calendar quarter against the agreed baseline.",
  },
  {
    q: "Can I upgrade between tiers, or layer Grow-With-You onto an existing tier?",
    a: "Yes to both. Many clients enter on Foundation, move to the Growth System within 6 to 12 months, and eventually upgrade to Full Execution as the team scales. The Performance Layer add-on can be attached to any tier at a quarterly review once the numbers are stable enough to set a clean baseline.",
  },
  {
    q: "What is not included?",
    a: "Paid advertising budgets are pass-through (you fund the spend directly). Third-party platform costs above standard tier are passed through at cost. Custom integrations beyond standard scope are quoted separately and only proceed with written approval.",
  },
  {
    q: "Do you guarantee outcomes?",
    a: "We do not guarantee revenue, because too many variables sit on your side. We do guarantee build-to-spec delivery, our published service levels, and ongoing iteration of the system. If the Growth System does not move the needle in 90 days post launch, we work for free until it does.",
  },
  {
    q: "Can I cancel?",
    a: "Monthly fees are rolling after the first 90 days. Cancel any time with 30 days notice. You keep the website, CRM, data and all platform accounts. We hand over cleanly. Performance share already earned through the cancellation date remains payable.",
  },
];

function CheckCell({ on, label }) {
  return on ? (
    <Check className="w-4 h-4 text-gold mx-auto" aria-label={`${label} included`} />
  ) : (
    <Minus className="w-4 h-4 text-zinc-700 mx-auto" aria-label={`${label} not included`} />
  );
}

export default function Pricing() {
  // Per-service Service schema (JSON-LD) so each à la carte service is
  // individually indexable by Google with its concrete price.
  const SERVICE_SCHEMA = {
    "@context": "https://schema.org",
    "@graph": ALA_CARTE.map((s) => {
      // Parse "From AU$2,997" / "AU$2,497/mo" / "AU$997" → numeric value
      const priceMatch = s.price.match(/[\d,]+/);
      const priceNum = priceMatch ? priceMatch[0].replace(/,/g, "") : null;
      const isMonthly = /\/mo/i.test(s.price);
      const offer = {
        "@type": "Offer",
        priceCurrency: "AUD",
        availability: "https://schema.org/InStock",
        url: `https://touliosadvisory.com/pricing#${s.id}`,
        ...(priceNum
          ? {
              price: priceNum,
              priceSpecification: isMonthly
                ? {
                    "@type": "UnitPriceSpecification",
                    price: priceNum,
                    priceCurrency: "AUD",
                    billingDuration: "P1M",
                  }
                : undefined,
            }
          : {}),
      };
      return {
        "@type": "Service",
        "@id": `https://touliosadvisory.com/pricing#${s.id}`,
        name: s.name,
        description: s.desc,
        serviceType: "Business Advisory",
        provider: { "@id": "https://touliosadvisory.com/#organization" },
        areaServed: { "@type": "Country", name: "Australia" },
        offers: offer,
      };
    }),
  };

  // FAQPage schema (JSON-LD) so Google can render each FAQ as an
  // expandable rich result directly in the search listing.
  const FAQ_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://touliosadvisory.com/pricing#faq",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(SERVICE_SCHEMA) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA) }}
      />
      {/* HERO */}
      <section className="container-x pt-24 md:pt-32 pb-12" data-testid="pricing-hero">
        <div className="overline">Pricing</div>
        <h1 className="mt-6 max-w-4xl font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl text-balance">
          Transparent fees. Fixed scope. Aligned upside.
        </h1>
        <p className="mt-8 max-w-2xl text-zinc-300 leading-relaxed text-lg">
          Three productised tiers from a modern website to a fully run growth
          system, plus 12 à la carte services priced to undercut the typical
          Australian agency by 30 to 70 percent. Performance-aligned
          Grow-With-You for operators who want maximum alignment. Every scope
          is quoted in writing after the Strategy Call.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Link to="/contact" className="btn-gold" data-testid="pricing-cta-hero">
            Book a Strategy Call <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="#services" className="btn-outline-white" data-testid="pricing-cta-services">
            See à la carte services <ArrowUpRight className="w-4 h-4" />
          </a>
          <a href="#grow-with-you" className="btn-ghost" data-testid="pricing-cta-gwy">
            Grow-With-You <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* TIER CARDS */}
      <section className="section-y" data-testid="pricing-tiers">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {TIERS.map((t, i) => {
              const featured = t.badge === "Most popular";
              return (
                <Reveal key={t.id} delay={i * 80}>
                  <div
                    className={`relative h-full flex flex-col p-8 md:p-10 transition-colors duration-300 ${
                      featured
                        ? "bg-[#0d0d0d] border border-gold/50 lg:scale-[1.02] z-10"
                        : "surface surface-hover"
                    }`}
                    data-testid={`tier-card-${t.id}`}
                  >
                    {featured && (
                      <div
                        aria-hidden
                        className="absolute -top-px left-0 right-0 h-px gold-line"
                      />
                    )}
                    {t.badge && (
                      <div
                        className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-gold text-black px-3 py-1 text-[11px] tracking-[0.18em] uppercase font-medium"
                        data-testid={`tier-badge-${t.id}`}
                      >
                        <Star className="w-3 h-3" />
                        {t.badge}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="overline">{String(i + 1).padStart(2, "0")}</span>
                      <t.icon className="w-5 h-5 text-gold" />
                    </div>

                    <h3 className="mt-8 text-2xl md:text-3xl font-display font-light tracking-tight">
                      {t.name}
                    </h3>
                    <div className="mt-2 text-sm text-gold tracking-tight">{t.subtitle}</div>

                    <p className="mt-5 text-zinc-400 leading-relaxed text-[15px]">{t.pitch}</p>

                    <div className="mt-8 pt-8 border-t border-white/[0.06]">
                      <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500">Setup</div>
                      <div className="mt-2 font-display text-3xl tracking-tight">{t.setup}</div>
                      <div className="mt-6 text-[11px] tracking-[0.2em] uppercase text-zinc-500">
                        Ongoing
                      </div>
                      <div className="mt-2 flex items-baseline gap-2">
                        <span className="font-display text-3xl tracking-tight">{t.monthly}</span>
                        <span className="text-sm text-zinc-500">{t.monthlyNote}</span>
                      </div>
                    </div>

                    <ul className="mt-8 space-y-3 text-sm text-zinc-300 flex-1">
                      {t.features.map((f) => (
                        <li key={f} className="flex items-start gap-3">
                          <Check className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-8 pt-6 border-t border-white/[0.06]">
                      <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500">
                        Outcome
                      </div>
                      <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{t.outcome}</p>
                    </div>

                    <Link
                      to={t.cta.to}
                      data-testid={`tier-cta-${t.id}`}
                      className={`mt-8 ${
                        featured ? "btn-gold" : "btn-outline-white"
                      } w-full justify-center`}
                    >
                      {t.cta.label}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <p className="mt-10 text-center text-xs text-zinc-500 max-w-2xl mx-auto leading-relaxed">
            Prices shown are indicative starting points in AUD, exclusive of
            GST. Final fixed fee is quoted in writing after the Strategy
            Call. No equity, no commissions, no per-lead fees. For a
            performance-aligned alternative, see Grow-With-You below.
          </p>
        </div>
      </section>

      {/* ===================== À LA CARTE SERVICES ===================== */}
      <section
        id="services"
        className="section-y bg-[#070707] border-y border-white/[0.05] scroll-mt-24"
        data-testid="ala-carte"
      >
        <div className="container-x">
          <SectionHeading
            eyebrow="À la carte · Individual services"
            title="Buy the piece you need, not the whole package."
            description="Every service below is priced to beat the typical Australian agency or consultancy by a meaningful margin. Fixed-fee, fixed-scope, no engagement minimums."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.05]">
            {ALA_CARTE.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 80}>
                <div
                  className="relative bg-ink p-6 md:p-8 h-full flex flex-col"
                  data-testid={`service-${s.id}`}
                >
                  {s.tag && (
                    <div className="absolute top-0 right-0 bg-gold text-black text-[10px] tracking-[0.2em] uppercase px-3 py-1 font-medium">
                      {s.tag}
                    </div>
                  )}
                  <div className="flex items-start justify-between">
                    <s.icon className="w-5 h-5 text-gold" />
                    <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">
                      {s.timeframe}
                    </div>
                  </div>
                  <h3 className="mt-6 text-xl font-display tracking-tight text-white">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{s.desc}</p>

                  <div className="mt-6 pt-6 border-t border-white/[0.06] mt-auto">
                    <div className="font-display text-2xl tracking-tight text-white">
                      {s.price}
                    </div>
                    <div className="mt-1 text-[11px] text-zinc-500">{s.market}</div>
                    <div className="mt-2 text-[11px] text-gold tracking-[0.15em] uppercase">
                      {s.savings}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Competitive comparison strip */}
          <Reveal delay={120}>
            <div
              className="mt-16 border border-gold/30 bg-[#0a0a0a] p-6 md:p-10"
              data-testid="competition-strip"
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-8">
                <div className="lg:flex-1">
                  <div className="overline flex items-center gap-2">
                    <Target className="w-3.5 h-3.5" /> Why we&rsquo;re cheaper
                  </div>
                  <h3 className="mt-4 font-display text-2xl md:text-3xl tracking-tight text-balance">
                    Built lean. Senior team. No overhead theatre.
                  </h3>
                  <p className="mt-4 text-zinc-400 leading-relaxed text-[15px] max-w-2xl">
                    Traditional agencies and boutique consultancies bake in
                    account managers, sales teams, and city-CBD rent. Toulios
                    is intentionally small. The senior consultant you meet on
                    the Strategy Call does the work. That structural choice
                    lets us undercut the market by 30 to 70 percent on every
                    service while delivering a more senior outcome.
                  </p>
                </div>
                <div className="lg:w-px lg:h-32 lg:bg-white/[0.06] hidden lg:block" />
                <div className="lg:w-72 lg:flex-shrink-0">
                  <div className="overline">Indicative annual savings</div>
                  <div className="mt-4 space-y-3">
                    {[
                      { k: "Website + CRM + SEO", v: "AU$6k–$25k" },
                      { k: "Fractional CMO/CFO (12 mo)", v: "AU$30k–$150k" },
                      { k: "Full Growth System build", v: "AU$15k–$50k" },
                    ].map((r) => (
                      <div
                        key={r.k}
                        className="flex items-baseline justify-between gap-3 border-l border-gold/30 pl-3"
                      >
                        <div className="text-xs text-zinc-400">{r.k}</div>
                        <div className="font-display text-sm text-gold whitespace-nowrap">{r.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <p className="mt-10 text-center text-xs text-zinc-500 max-w-2xl mx-auto leading-relaxed">
            Market price ranges are sourced from public agency and consultancy
            rate cards across the Australian SME sector. Savings illustrative
            of the lower bound of each tier. Every Toulios scope is quoted in
            writing as a fixed fee after the Strategy Call.
          </p>
        </div>
      </section>

      {/* ===================== GROW-WITH-YOU ===================== */}
      <section
        id="grow-with-you"
        className="section-y bg-[#070707] border-y border-white/[0.05] scroll-mt-24"
        data-testid="grow-with-you"
      >
        <div className="container-x">
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="overline flex items-center gap-2">
                  <Handshake className="w-3.5 h-3.5" /> Performance-aligned engagement
                </div>
                <h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl lg:text-6xl text-balance leading-[1.05]">
                  Grow-With-You.
                </h2>
                <p className="mt-6 text-lg text-gold tracking-tight">
                  We earn more when you do.
                </p>
                <p className="mt-6 text-zinc-400 leading-relaxed text-[15px]">
                  {GROW_WITH_YOU.pitch}
                </p>

                <div className="mt-10 space-y-6">
                  <div>
                    <div className="overline">Best fit for</div>
                    <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
                      {GROW_WITH_YOU.fitFor}
                    </p>
                  </div>
                  <div>
                    <div className="overline">Not a fit for</div>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                      {GROW_WITH_YOU.notFitFor}
                    </p>
                  </div>
                </div>

                <Link
                  to={GROW_WITH_YOU.cta.to}
                  className="btn-gold mt-10"
                  data-testid="grow-with-you-cta"
                >
                  {GROW_WITH_YOU.cta.label}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={120}>
                <div className="relative border border-gold/30 bg-[#0a0a0a]">
                  <div aria-hidden className="absolute inset-x-0 top-0 h-px gold-line" />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/[0.05]">
                    {GROW_WITH_YOU.pillars.map((p, i) => (
                      <div
                        key={p.title}
                        className="bg-[#0a0a0a] p-8 md:p-10"
                        data-testid={`gwy-pillar-${i}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="overline">{String(i + 1).padStart(2, "0")}</span>
                          <p.icon className="w-5 h-5 text-gold" />
                        </div>
                        <h3 className="mt-6 text-lg md:text-xl font-display tracking-tight">
                          {p.title}
                        </h3>
                        <p className="mt-3 text-zinc-400 leading-relaxed text-sm">
                          {p.body}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-white/[0.06] bg-[#0d0d0d] p-8 md:p-10">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      <div>
                        <div className="overline">Fixed base</div>
                        <div className="mt-3 font-display text-2xl tracking-tight">By scope</div>
                        <p className="mt-2 text-xs text-zinc-500 leading-relaxed">
                          Reduced relative to Full Execution. Quoted on the Strategy Call.
                        </p>
                      </div>
                      <div>
                        <div className="overline">Performance share</div>
                        <div className="mt-3 font-display text-2xl tracking-tight">% of uplift</div>
                        <p className="mt-2 text-xs text-zinc-500 leading-relaxed">
                          On incremental gross profit above the written baseline.
                        </p>
                      </div>
                      <div>
                        <div className="overline">KPI bonuses</div>
                        <div className="mt-3 font-display text-2xl tracking-tight">On outcomes</div>
                        <p className="mt-2 text-xs text-zinc-500 leading-relaxed">
                          Modest one-time bonuses tied to agreed KPI uplifts.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <p
                  className="mt-6 text-xs text-zinc-500 leading-relaxed max-w-2xl"
                  data-testid="gwy-legal"
                >
                  Grow-With-You is a <span className="text-zinc-300">consulting fee structure</span>{" "}
                  tied to measurable performance outcomes. It is not equity, a
                  profit-share investment, a securities offering, or financial
                  product advice. Toulios Advisory Group does not hold an
                  Australian Financial Services Licence. Baseline and
                  attribution methodology are agreed in writing prior to
                  commencement. General management advisory only.
                </p>
              </Reveal>
            </div>
          </div>

          {/* Performance Layer add-on note */}
          <Reveal delay={200}>
            <div
              className="mt-16 border border-white/[0.06] p-6 md:p-8 bg-[#0a0a0a]"
              data-testid="performance-layer-addon"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                <div className="lg:col-span-8">
                  <div className="overline">Performance Layer · Add-on</div>
                  <h3 className="mt-4 font-display text-xl md:text-2xl tracking-tight">
                    Already on Foundation, Growth System or Full Execution?
                  </h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-2xl">
                    The Grow-With-You performance share can be layered onto
                    any tier as an add-on. Same baseline-and-attribution rules
                    apply. Switch into the layer at any quarterly review once
                    your numbers are stable enough to set a clean baseline.
                  </p>
                </div>
                <div className="lg:col-span-4 lg:text-right">
                  <Link to="/contact" className="btn-outline-white" data-testid="performance-layer-cta">
                    Discuss the add-on <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section
        id="compare"
        className="section-y bg-[#070707] border-y border-white/[0.05] scroll-mt-24"
        data-testid="pricing-compare"
      >
        <div className="container-x">
          <Reveal>
            <SectionHeading
              eyebrow="Full comparison"
              title="Side by side."
              description="What sits inside each tier. The full operating picture, at a glance."
            />
          </Reveal>

          <div className="mt-16 overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
            <table className="w-full min-w-[760px] border-collapse" data-testid="compare-table">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="text-left py-5 pr-6 align-bottom">
                    <span className="overline">Feature</span>
                  </th>
                  {TIERS.map((t) => (
                    <th key={t.id} className="text-center py-5 px-4 align-bottom min-w-[160px]">
                      <div className="font-display text-base md:text-lg tracking-tight">
                        {t.name}
                      </div>
                      <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-zinc-500">
                        {t.subtitle}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_GROUPS.map((g) => (
                  <Fragment key={g.group}>
                    <tr className="bg-[#0a0a0a]">
                      <td
                        colSpan={4}
                        className="py-4 pr-6 text-[11px] tracking-[0.22em] uppercase text-gold"
                      >
                        {g.group}
                      </td>
                    </tr>
                    {g.rows.map((r) => (
                      <tr
                        key={`${g.group}-${r.feature}`}
                        className="border-b border-white/[0.04]"
                      >
                        <td className="py-4 pr-6 text-sm text-zinc-300">{r.feature}</td>
                        {r.tiers.map((on, idx) => (
                          <td key={`${g.group}-${r.feature}-tier-${idx}`} className="py-4 px-4 text-center">
                            <CheckCell on={on} label={r.feature} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}

                {/* Pricing row */}
                <tr className="border-t-2 border-gold/40">
                  <td className="py-6 pr-6">
                    <span className="overline">Starting price</span>
                  </td>
                  {TIERS.map((t) => (
                    <td key={t.id} className="py-6 px-4 text-center">
                      <div className="font-display text-lg tracking-tight">{t.setup}</div>
                      <div className="mt-1 text-xs text-zinc-500">
                        then {t.monthly} {t.monthlyNote}
                      </div>
                    </td>
                  ))}
                </tr>

                <tr>
                  <td className="py-6 pr-6"></td>
                  {TIERS.map((t) => (
                    <td key={t.id} className="py-6 px-4 text-center">
                      <Link
                        to={t.cta.to}
                        data-testid={`compare-cta-${t.id}`}
                        className={`${
                          t.badge === "Most popular" ? "btn-gold" : "btn-outline-white"
                        } w-full justify-center text-xs px-3 py-2`}
                      >
                        Book a Call
                      </Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="section-y" data-testid="pricing-outcomes">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Value benchmark"
              title="What good looks like, after 90 days."
              description="The typical first-quarter outcomes for clients who complete the build and run the system as designed. These compound from there."
            />
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/[0.05]">
            {OUTCOMES.map((o, i) => (
              <Reveal key={o.v} delay={i * 80} className="bg-ink p-8 md:p-10">
                <div className="font-display text-3xl md:text-4xl tracking-tight">{o.k}</div>
                <p className="mt-4 text-zinc-400 leading-relaxed">{o.v}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-[#070707] border-y border-white/[0.05]" data-testid="pricing-trust">
        <div className="container-x py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { k: "Fixed fee", v: "Quoted in writing after the Strategy Call." },
            { k: "No surprises", v: "No equity, no commissions, no per-lead fees." },
            { k: "Cancel any time", v: "Monthly rolling after the first 90 days." },
          ].map((t) => (
            <div key={t.k} className="flex items-start gap-4">
              <ShieldCheck className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
              <div>
                <div className="overline">{t.k}</div>
                <p className="mt-2 text-zinc-300 text-sm leading-relaxed">{t.v}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y" data-testid="pricing-faq-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="FAQ"
              title="Pricing questions, answered."
              description="The questions every operator asks before booking a Strategy Call."
            />
          </div>
          <div className="lg:col-span-7">
            <FAQ items={FAQS} testIdPrefix="pricing-faq" />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section-y" data-testid="pricing-final-cta">
        <div className="container-x">
          <div className="relative overflow-hidden border border-white/[0.06] bg-gradient-to-br from-[#0d0d0d] to-[#070707] p-10 md:p-16 lg:p-20">
            <div
              aria-hidden
              className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full"
              style={{ background: "radial-gradient(closest-side, rgba(212,175,55,0.18), transparent 70%)" }}
            />
            <div aria-hidden className="absolute inset-x-0 top-0 h-px gold-line" />

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <div className="overline">Not sure which tier?</div>
                <h3 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-display font-light tracking-tight text-balance">
                  Book a Strategy Call. We will tell you straight.
                </h3>
                <p className="mt-6 max-w-2xl text-zinc-400 leading-relaxed">
                  A senior advisor reviews your current setup and recommends
                  the right place to start, including whether starting at all
                  is the right move. Complimentary, confidential, no pitch
                  deck.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
                <Link to="/contact" className="btn-gold" data-testid="pricing-final-cta-book">
                  Book a Strategy Call
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/what-we-do" className="btn-outline-white">
                  See the full firm
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
